/**
 * A collection profile (`136:950`): per media kind, the periods a post is refreshed through
 * (`KindCards`, audio among them) and night reduction below them, with the rail of accounts
 * using it and the save's projected impact beside them. The profile is the whole
 * configuration; nothing here references a shared catalogue. Saving re-plans every account on the profile, so it goes through
 * `ReplanConfirmation`.
 *
 * `POST .../replan-preview`'s body is `AssumedAccountIn`, which requires a `rate_per_day` for
 * every enabled lifecycle kind (POST, REEL) and a `comments_per_item` -- `_priced_by_assumed_shape`
 * (`api/src/socialsrc_api/routers/tracking.py`) 422s without one, since §6.6 gives an assumed
 * shape no fallback. `previewBody` below fills both from the profile listing's own `price.shape`
 * (`{rate_per_day, comments_per_item}`), the one standard shape the listing itself prices with,
 * so the preview and the listing's own figure price from one shape rather than two.
 *
 * There is no single-profile route, so the profile is read off the listing and found by id.
 */

import { useEffect, useId, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import { EmbeddedSwitcher } from '../../../chrome/entity/EntitySwitcher';
import { ApiFailureError, type ApiFailure } from '../../../lib/http/errors';
import { Page, PageSection } from '../../../chrome/page';
import { useBreadcrumbLeaf } from '../../../chrome/shell';
import { QueryStates } from '../../../chrome/state';
import { Button } from '../../../ui/button';
import { Card, CardContent } from '../../../ui/card';
import { Field, FieldError, FieldLabel } from '../../../ui/field';
import { Input } from '../../../ui/input';
import { Separator } from '../../../ui/separator';
import { Skeleton } from '../../../ui/skeleton';
import { useApiClient } from '../../session/client';
import { useCan } from '../../session/orgContext';
import { useFundedWindow } from '../../tenant/balance';
import { GET_ROUTES, routePath } from '../../../lib/http/routes';
import { profileSchema } from '../api/profileSchemas';
import {
  REPLAN_PREVIEW_PATH,
  assumedShapeOf,
  audioForWire,
  mediaForWire,
  replanPreviewSchema,
  type AssumedShape,
} from '../api/replanPreview';
import { AccountsRail } from './profile/AccountsRail';
import { KindCards, type AudioRow, type KindRow, type MediaKindName } from './profile/KindCards';
import {
  NightReduction,
  NIGHT_REDUCTION_SEED,
  nightReductionValid,
  type NightReductionValue,
} from './profile/NightReduction';
import { profileCatalogue } from './profile/messages';
import {
  MIN_AUDIO_FREQUENCY_SECONDS,
  MIN_FREQUENCY_SECONDS,
  nextPeriod,
  periodValid,
} from './profile/periods';
import { ReplanConfirmation } from './profile/ReplanConfirmation';
import { SaveImpactCard } from './profile/SaveImpactCard';

export type { MediaKindName } from './profile/KindCards';

export interface MediaRow extends KindRow {
  comment_cap_per_post: number | null;
}

type Profile = z.infer<typeof profileSchema>;

/** The PUT answers `MonitoringProfileOut`, unpriced and with no read-time `usage_count`. */
const savedProfileSchema = profileSchema.omit({ usage_count: true, price: true });

export const PROFILE_QUERY_KEY = (tenantId: string, profileId: string) =>
  ['control', 'monitoring-profile', tenantId, profileId] as const;

const STORY_DEFAULT_SECONDS = 3600;
const AUDIO_OFF: AudioRow = { enabled: false, periods: [], page_limit: null };
/** What switching audio on seeds when it holds no period: every 12h for 3 days. */
const AUDIO_SEED = { frequency_seconds: 43_200, duration_seconds: 259_200, color: null } as const;

/** Never actually sent -- every query reading it is `enabled: complete`, which requires
 *  `loaded` too. Exists only so `previewBody` has a value before the profile has loaded. */
const UNLOADED_SHAPE: AssumedShape = {
  platform: 'instagram',
  rate_per_day: { POST: 0, REEL: 0 },
  comments_per_item: 0,
  tail_comments_per_item: null,
  reels_in_feed: null,
  night_window: null,
};

/** An enabled row names what it collects, in values the api accepts. */
function rowComplete(row: MediaRow): boolean {
  if (row.enabled === 'off') return true;
  if (row.media_kind === 'STORY') {
    return (
      row.story_interval_seconds !== null && row.story_interval_seconds >= MIN_FREQUENCY_SECONDS
    );
  }
  return row.periods.length > 0 && row.periods.every((period) => periodValid(period));
}

function audioComplete(audio: AudioRow): boolean {
  if (!audio.enabled) return true;
  return (
    audio.periods.length > 0 &&
    audio.periods.every((period) => periodValid(period, MIN_AUDIO_FREQUENCY_SECONDS))
  );
}

function nightReductionOf(profile: Profile): NightReductionValue {
  return {
    night_reduction_enabled: profile.night_reduction_enabled,
    night_reduction_max_interval_seconds: profile.night_reduction_max_interval_seconds,
    night_reduction_start_local: profile.night_reduction_start_local,
    night_reduction_stop_local: profile.night_reduction_stop_local,
  };
}

function usageSentence(usageCount: number): string {
  const accounts = profileCatalogue.plural('usage.accounts.other', usageCount, {
    count: usageCount,
  });
  return profileCatalogue.t('usage.sentence', { accounts });
}

export function ProfileEditor(props: { tenantId: string; profileId: string }): ReactNode {
  const client = useApiClient();
  const queryClient = useQueryClient();
  // §17.15: an `Analista` sees neither `Descartar` nor `Rever e aplicar`.
  const canWrite = useCan('control_plane.write');

  const profileQuery = useQuery<Profile, ApiFailureError>({
    queryKey: PROFILE_QUERY_KEY(props.tenantId, props.profileId),
    queryFn: async () => {
      const list = await client.getParsed(
        routePath(GET_ROUTES.monitoringProfilesEditor, { tenantId: props.tenantId }),
        GET_ROUTES.monitoringProfilesEditor.schema,
      );
      const found = list.items.find((item) => item.id === props.profileId);
      if (found === undefined) throw new ApiFailureError({ kind: 'not-found' });
      return found;
    },
  });

  const fundedWindowQuery = useFundedWindow(props.tenantId);

  const [media, setMedia] = useState<MediaRow[] | null>(null);
  const [audio, setAudio] = useState<AudioRow | null>(null);
  const [nightReduction, setNightReduction] = useState<NightReductionValue | null>(null);
  const nameId = useId();
  const [name, setName] = useState('');
  useBreadcrumbLeaf(name || profileQuery.data?.name);
  const [description, setDescription] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const loaded = profileQuery.data;

  function reset(profile: Profile): void {
    setMedia(profile.media);
    setAudio(profile.audio);
    setName(profile.name);
    setDescription(profile.description);
    setNightReduction(nightReductionOf(profile));
  }

  // Seeds the editable copies once; a background refetch never clobbers an edit in progress.
  useEffect(() => {
    if (loaded && media === null) reset(loaded);
  }, [loaded, media]);

  const hasChanges =
    loaded !== undefined &&
    media !== null &&
    audio !== null &&
    nightReduction !== null &&
    (name !== loaded.name ||
      description !== loaded.description ||
      JSON.stringify(media) !== JSON.stringify(loaded.media) ||
      JSON.stringify(audio) !== JSON.stringify(loaded.audio) ||
      JSON.stringify(nightReduction) !== JSON.stringify(nightReductionOf(loaded)));

  const complete =
    media !== null &&
    media.every(rowComplete) &&
    audio !== null &&
    audioComplete(audio) &&
    nightReduction !== null &&
    nightReductionValid(nightReduction);

  // `loaded` is always defined once `complete` can be true: `media` is only ever set from
  // `reset(loaded)`, so a non-null `media` means a `loaded` profile produced it. The fallback
  // below is never actually sent -- `enabled: complete` gates the request on `loaded` too.
  const previewBody: AssumedShape & { media?: unknown[]; audio?: unknown } =
    loaded === undefined
      ? UNLOADED_SHAPE
      : media === null || audio === null
        ? assumedShapeOf(loaded.price)
        : {
            ...assumedShapeOf(loaded.price),
            media: mediaForWire(media),
            audio: audioForWire(audio),
          };

  const previewQuery = useQuery<z.infer<typeof replanPreviewSchema>, ApiFailureError>({
    queryKey: [
      'control',
      'monitoring-profile-replan-preview',
      props.tenantId,
      props.profileId,
      JSON.stringify(previewBody),
    ],
    queryFn: () =>
      client.postParsed(
        routePath(
          { path: REPLAN_PREVIEW_PATH },
          { tenantId: props.tenantId, profileId: props.profileId },
        ),
        previewBody,
        replanPreviewSchema,
      ),
    enabled: complete && loaded !== undefined,
    staleTime: Infinity,
  });

  const saveMutation = useMutation<z.infer<typeof savedProfileSchema>, ApiFailureError>({
    mutationFn: () =>
      client.putParsed(
        `/tenants/${props.tenantId}/monitoring-profiles/${props.profileId}`,
        {
          name: name.trim(),
          description,
          media: mediaForWire(media ?? []),
          audio: audioForWire(audio ?? AUDIO_OFF),
          ...(nightReduction ?? NIGHT_REDUCTION_SEED),
        },
        savedProfileSchema,
      ),
    onSuccess: (updated) => {
      queryClient.setQueryData<Profile>(
        PROFILE_QUERY_KEY(props.tenantId, props.profileId),
        (previous) => previous && { ...previous, ...updated },
      );
      setMedia(updated.media);
      setAudio(updated.audio);
      void queryClient.invalidateQueries({
        queryKey: PROFILE_QUERY_KEY(props.tenantId, props.profileId),
      });
      void queryClient.invalidateQueries({
        queryKey: ['control', 'monitoring-profile-replan-preview', props.tenantId, props.profileId],
      });
    },
  });

  function patchRow(kind: MediaKindName, patch: Partial<MediaRow>): void {
    setMedia((rows) =>
      rows === null
        ? rows
        : rows.map((row) => (row.media_kind === kind ? { ...row, ...patch } : row)),
    );
  }

  function toggleEnabled(kind: MediaKindName): void {
    const row = media?.find((candidate) => candidate.media_kind === kind);
    if (row === undefined) return;
    if (row.enabled !== 'off') {
      patchRow(kind, { enabled: 'off' });
      return;
    }
    patchRow(kind, {
      enabled: 'on',
      ...(kind === 'STORY'
        ? { story_interval_seconds: row.story_interval_seconds ?? STORY_DEFAULT_SECONDS }
        : { periods: row.periods.length > 0 ? row.periods : [nextPeriod([])] }),
    });
  }

  function patchAudio(patch: Partial<AudioRow>): void {
    setAudio((current) => (current === null ? current : { ...current, ...patch }));
  }

  function toggleAudio(): void {
    setAudio((current) => {
      if (current === null) return current;
      if (current.enabled) return { ...current, enabled: false };
      return {
        ...current,
        enabled: true,
        periods: current.periods.length > 0 ? current.periods : [{ ...AUDIO_SEED }],
      };
    });
  }

  // The only 409 the save raises is a name another profile of the organisation already holds.
  const nameTaken = saveMutation.error?.failure.kind === 'conflict';
  const nameValid = media === null || name.trim() !== '';
  const nameError = !nameValid
    ? profileCatalogue.t('name.required')
    : nameTaken
      ? profileCatalogue.t('name.taken')
      : null;
  const saveFailure: ApiFailure | null = nameTaken
    ? { kind: 'invalid', field: 'name', message: profileCatalogue.t('name.taken') }
    : (saveMutation.error?.failure ?? null);

  return (
    <QueryStates query={profileQuery} empty={null}>
      {(profile) => (
        <Page>
          {/* Not `PageHeader`: its title is sr-only (a screen's name is normally the topbar
           * trail's last crumb), but `EntityPage embedded` (`routes.ts`'s `ProfileEditorRoute`)
           * needs a real, visible heading to hang `EmbeddedSwitcher` off -- the same shape
           * `Account.tsx` and `PostCard.tsx` draw their own headers in. */}
          <header className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <h1 className="min-w-0 text-title-md font-semibold">
              <EmbeddedSwitcher>
                <span className="min-w-0 truncate">{name || profile.name}</span>
              </EmbeddedSwitcher>
            </h1>
            <div className="flex items-center gap-2">
              <p className="max-w-[66ch] text-body text-muted-foreground">
                {usageSentence(profile.usage_count)}
              </p>
            </div>
            {canWrite && (
              <div data-slot="actions" className="flex flex-wrap items-center gap-2 md:ml-auto">
                <Button
                  type="button"
                  variant="outline"
                  disabled={!hasChanges}
                  onClick={() => loaded && reset(loaded)}
                >
                  {profileCatalogue.t('discard')}
                </Button>
                <Button
                  type="button"
                  disabled={!complete || !nameValid}
                  onClick={() => setConfirmOpen(true)}
                >
                  {profileCatalogue.t('save.open')}
                </Button>
              </div>
            )}
          </header>

          <Field data-invalid={nameError !== null} className="max-w-sm">
            <FieldLabel htmlFor={nameId}>{profileCatalogue.t('name.label')}</FieldLabel>
            <Input
              id={nameId}
              value={name}
              disabled={!canWrite}
              aria-invalid={nameError !== null}
              onChange={(event) => {
                setName(event.target.value);
                saveMutation.reset();
              }}
            />
            {nameError !== null && <FieldError>{nameError}</FieldError>}
          </Field>

          {/* `card={false}`: each kind already draws its own `Card` (`KindCardFrame`), and the
           * right rail draws its own below -- `PageSection`'s default Card would wrap both in a
           * third, a Card holding Cards. */}
          <PageSection title={profileCatalogue.t('media.title')} card={false}>
            {media === null ? (
              <div className="flex flex-col gap-4" data-testid="media-loading">
                <Skeleton className="h-32 w-full" />
                <Skeleton className="h-32 w-full" />
              </div>
            ) : media.length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="media-empty">
                {profileCatalogue.t('media.empty')}
              </p>
            ) : (
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                {/* §17.15: a reader sees the periods it cannot change. */}
                <fieldset
                  disabled={!canWrite}
                  className="flex min-w-0 flex-col gap-4 lg:col-span-2"
                >
                  {audio !== null && (
                    <KindCards
                      rows={media}
                      onChange={patchRow}
                      onToggle={toggleEnabled}
                      audio={audio}
                      onAudioChange={patchAudio}
                      onAudioToggle={toggleAudio}
                    />
                  )}
                  {nightReduction === null ? (
                    <Skeleton className="h-24 w-full" />
                  ) : (
                    <Card>
                      <CardContent>
                        <NightReduction value={nightReduction} onChange={setNightReduction} />
                      </CardContent>
                    </Card>
                  )}
                </fieldset>

                <div className="min-w-0 lg:col-span-1">
                  <Card>
                    <CardContent className="flex flex-col gap-5">
                      <AccountsRail tenantId={props.tenantId} profileId={props.profileId} />
                      <Separator />
                      <SaveImpactCard
                        preview={previewQuery.data ?? null}
                        previewFailure={previewQuery.error?.failure ?? null}
                        incomplete={!complete}
                        currentFundedUntil={fundedWindowQuery.data?.funded_until ?? null}
                      />
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}
          </PageSection>

          <ReplanConfirmation
            open={confirmOpen}
            onOpenChange={setConfirmOpen}
            accounts={previewQuery.data?.per_account ?? null}
            deltaCreditsPerDay={previewQuery.data?.delta_credits_per_day ?? null}
            currentFundedUntil={fundedWindowQuery.data?.funded_until ?? null}
            projectedFundedUntil={previewQuery.data?.funded_until ?? null}
            previewFailure={previewQuery.error?.failure ?? null}
            pending={saveMutation.isPending}
            failure={saveFailure}
            onConfirm={() => saveMutation.mutateAsync()}
          />
        </Page>
      )}
    </QueryStates>
  );
}
